import React from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from "react-router-dom";
import { DateTimeFormatter, LocalDateTime } from "@js-joda/core";
import Button from "../../../components/Button.jsx";

const DomainsConfirmationModal = ({
    isOpen,
    onConfirm,
    onClose,
    priceQuote,
    domainsToPay,
    project
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    if (!isOpen) return null;

    const subscription = project.subscription || {};
    const subscriptionDomainsCount = subscription.currentPackage?.maxDomains ?? 1;
    const extraPaidDomainsCount = subscription.extraPaidDomainsCount ?? 0;
    const endDateString = subscription.endDate
        ? LocalDateTime.parse(subscription.endDate).format(DateTimeFormatter.ofPattern('dd.MM.yyyy'))
        : null;

    const currencySymbol = priceQuote?.currencyCode === 'EUR' ? '€' : 'Kč';
    const renderAmount = (amount, forceCents = false) => ((amount ?? 0) / 100).toLocaleString('cs-CZ', {
        minimumFractionDigits: forceCents ? 2 : 0,
        maximumFractionDigits: 2,
    });

    const handleUpgradePlan = () => {
        navigate(`/project/${project.id}/settings/subscription`);
        onClose();
    };

    return ReactDOM.createPortal(
        <div className="modal__domains fixed inset-0 z-50 flex overflow-auto bg-black/50">
            <div className="relative bg-white dark:bg-gray-800 w-full max-w-lg m-auto flex-col flex app-surface rounded-lg shadow-lg">
                <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700/60">
                    <div className="flex justify-between items-center">
                        <div className="font-semibold text-lg text-gray-800 dark:text-gray-100">
                            {t('domains.confirm-title')}
                        </div>
                        <button
                            className="text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400"
                            onClick={onClose}
                        >
                            <div className="sr-only">Close</div>
                            <svg className="fill-current" width="16" height="16" viewBox="0 0 16 16">
                                <path
                                    d="M7.95 6.536l4.242-4.243a1 1 0 111.415 1.414L9.364 7.95l4.243 4.242a1 1 0 11-1.415 1.415L7.95 9.364l-4.243 4.243a1 1 0 01-1.414-1.415L6.536 7.95 2.293 3.707a1 1 0 011.414-1.414L7.95 6.536z"
                                ></path>
                            </svg>
                        </button>
                    </div>
                </div>
                <div className="px-5 py-4">
                    <div className="text-sm text-gray-600 mb-2">
                        <div className="mb-3 text-violet-600">
                            <strong>{t('domains.exceeded-domains-count')}</strong>
                        </div>

                        <div className="mb-4">
                            {t('domains.additional-domains-available')}
                            <ul className="list-disc pl-3.5 mt-1">
                                <li>{t('domains.first-extra-domain')}</li>
                                <li>{t('domains.other-extra-domain')}</li>
                            </ul>
                        </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 py-3.5 px-5 app-surface rounded-xl border-1 border-gray-400">
                        <div className="text-black dark:text-gray-100 font-bold mb-2">
                            {t('domains.title')}
                        </div>
                        <ul>
                            <li className="text-sm w-full flex justify-between py-1.5 border-b border-gray-200 dark:border-gray-700/60">
                                <div>{t('domains.included')}</div>
                                <div className="font-medium text-gray-800 dark:text-gray-100">{subscriptionDomainsCount}</div>
                            </li>
                            {extraPaidDomainsCount > 0 && (
                                <li className="text-sm w-full flex justify-between py-1.5 border-b border-gray-200 dark:border-gray-700/60">
                                    <div>{t('domains.extra-paid')}</div>
                                    <div className="font-medium text-gray-800 dark:text-gray-100">{extraPaidDomainsCount}</div>
                                </li>
                            )}
                            <li className="text-sm w-full flex justify-between pt-1.5">
                                <div>{t('domains.to-pay')}</div>
                                <div className="font-medium text-gray-800 dark:text-gray-100">{domainsToPay}</div>
                            </li>
                        </ul>
                    </div>

                    <div
                        className="mt-3 mb-3 w-full h-full text-left py-3 px-4 rounded-lg bg-white dark:bg-gray-800 border border-violet-400 dark:border-violet-500 shadow-sm">
                        <div className="flex flex-wrap items-center justify-between mb-0.5">
                            <div className="text-black font-bold text-gray-800 dark:text-gray-100">
                                <div>{t('domains.total-to-pay')} <span className={'font-normal text-sm text-violet-500'}>({domainsToPay})</span></div>
                            </div>
                            <div className="font-medium text-green-600 text-right">{renderAmount(priceQuote?.totalAmount)} {currencySymbol}</div>
                        </div>
                    </div>
                    {priceQuote && <div className="mb-3 app-surface rounded-lg border border-violet-300 bg-violet-50/60 px-4 py-3 text-sm dark:border-violet-700 dark:bg-violet-950/20">
                        <div className="flex justify-between gap-4"><span>{t('subscription.price-excluding-vat')}</span><strong>{renderAmount(priceQuote.netAmount)} {currencySymbol}</strong></div>
                        <div className="mt-1 flex justify-between gap-4"><span>{priceQuote.reverseCharge ? t('subscription.vat-reverse-charge') : t('subscription.vat-rate', {rate: priceQuote.vatPercentage})}</span><strong>{renderAmount(priceQuote.vatAmount)} {currencySymbol}</strong></div>
                        {(priceQuote.roundingAmount ?? 0) !== 0 && <div className="mt-1 flex justify-between gap-4"><span>{t('subscription.rounding')}</span><strong>{priceQuote.roundingAmount > 0 ? '+' : ''}{renderAmount(priceQuote.roundingAmount, true)} {currencySymbol}</strong></div>}
                        <div className="mt-2 flex justify-between gap-4 border-t border-violet-200 pt-2 font-semibold dark:border-violet-800"><span>{t('subscription.final-price-including-vat')}</span><strong>{renderAmount(priceQuote.totalAmount)} {currencySymbol}</strong></div>
                    </div>}
                    <div className="text-xs text-gray-500">
                        {t('domains.price-description', { endDateString })}
                    </div>
                </div>

                <div className="px-2.5 py-2 flex gap-1 border-t border-gray-200 dark:border-gray-700/60">
                    <Button variant="outline-red" onClick={handleUpgradePlan}>
                        {t('domains.change-tarif')}
                    </Button>
                    <Button variant="black" className="ml-auto" onClick={onConfirm} disabled={!priceQuote}>
                        {t('domains.pay')}
                    </Button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default DomainsConfirmationModal;
